import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy } from 'lucide-react';

import { cn } from '@/lib/utils';

type Props = {
  /** What is copied: the full address or hash. */
  value: string;
  /** What is shown when the full value is too long for the cell. */
  display?: string;
  /** Names the value for the copy button's accessible label. */
  label: string;
  className?: string;
};

/**
 * A wallet address or transaction hash with one-click copy. Operators paste
 * these into block explorers and support threads; retyping a 42-character
 * address is where transcription errors come from, so the copy is the point.
 */
export function CopyableValue({ value, display, label, className }: Props) {
  const { t } = useTranslation('manageCryptoPayments');
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      window.setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Clipboard access refused: the value stays readable on screen.
    }
  };

  const copyLabel = t('copy.action', { label });

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copyLabel}
      title={isCopied ? t('copy.copied') : copyLabel}
      className={cn(
        'text-foreground hover:text-primary group inline-flex max-w-full items-center gap-1.5 text-left text-sm transition-colors',
        className,
      )}
    >
      <span className="break-all font-mono">{display ?? value}</span>
      {isCopied ? (
        <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
      ) : (
        <Copy className="text-muted-foreground h-3.5 w-3.5 shrink-0 opacity-60 transition-opacity group-hover:opacity-100" />
      )}
    </button>
  );
}
